import { useEffect } from "react"

import { flattenSubpageTree } from "@productlane/lib/src/help-center/subpage-tree"

interface ArticleRow {
	id: string
	portalInstanceId?: string | null
	subpages?: readonly ArticleRow[] | null
}

interface GroupRow {
	helpCenterArticles?: readonly ArticleRow[] | null
}

// Root articles live on `portalInstanceId === null`, so `undefined` is the only
// way to say "no article with that id".
export function findArticlePortalInstanceId(
	groups: readonly GroupRow[],
	articleId: string,
): string | null | undefined {
	if (!articleId) return undefined
	for (const group of groups) {
		for (const article of group.helpCenterArticles ?? []) {
			if (article.id === articleId) return article.portalInstanceId ?? null
			const subpage = flattenSubpageTree(article.subpages).find(
				(row) => row.id === articleId,
			)
			if (subpage) return subpage.portalInstanceId ?? null
		}
	}
	return undefined
}

// Runs only when the URL's article changes. Reacting to the active portal as well
// undid every switch made in another tab, since the choice syncs through localStorage.
export function useFollowArticlePortal({
	enabled,
	articlePortalInstanceId,
	activePortalInstanceId,
	setActivePortalInstanceId,
}: {
	enabled: boolean
	articlePortalInstanceId: string | null | undefined
	activePortalInstanceId: string | null
	setActivePortalInstanceId: (id: string | null) => void
}) {
	useEffect(() => {
		if (!enabled) return
		if (articlePortalInstanceId === undefined) return
		if (articlePortalInstanceId === activePortalInstanceId) return
		setActivePortalInstanceId(articlePortalInstanceId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, articlePortalInstanceId])
}
